// Shared display config for the Department Income Tracker - cards, chart and table all key
// off this so "Classroom is green" means the same thing everywhere on the page.
//
// The department LIST itself is never hardcoded here - it always comes from the API's
// meta.departments, which mirrors TRACKED_DEPT_IDS in the backend's
// department_income_tracker.query.js. Add a department there (e.g. dept_id 57) and it shows
// up here automatically: a new card, a new chart series, a new table column - no frontend
// change needed. What IS below is purely cosmetic (a nicer order/color for the 6 departments
// we know about today) and is written to degrade gracefully for anything not in that list,
// rather than break or silently disappear.

// Preferred order for the 6 known departments, matching the source spreadsheet (OGD,
// Classroom, Foundation, Little Genie, CSD, Speak Pro). Anything not listed here - i.e. a
// department added to the backend after this was written - is appended after these, in
// whatever order the API returns it (dept_id ascending), instead of being sorted to the front
// by an `indexOf` of -1.
const DEPT_DISPLAY_ORDER = [11, 20, 3, 21, 12, 22];

const DEPT_COLORS = {
  11: '#6366F1', // OGD - indigo
  20: '#10B981', // Classroom - emerald
  3: '#F59E0B', // Foundation - amber
  21: '#EC4899', // Little Genie - pink
  12: '#06B6D4', // CSD - cyan
  22: '#0D9488', // Speak Pro - teal
};

// Distinct, pre-picked colors for any department beyond the 5 curated above, assigned by a
// stable hash of deptId so the same unlisted department always gets the same color across
// reloads/filters, and two different unlisted departments don't collide on the same gray.
const FALLBACK_PALETTE = ['#8B5CF6', '#14B8A6', '#F97316', '#84CC16', '#0EA5E9', '#D946EF', '#EAB308', '#F43F5E'];

export const getDeptColor = (deptId) => {
  if (DEPT_COLORS[deptId]) return DEPT_COLORS[deptId];
  const hash = String(deptId).split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return FALLBACK_PALETTE[hash % FALLBACK_PALETTE.length];
};

const orderIndex = (deptId) => {
  const i = DEPT_DISPLAY_ORDER.indexOf(deptId);
  return i === -1 ? DEPT_DISPLAY_ORDER.length : i;
};

// Short label for the Daily Breakdown table's column header - the full name (e.g. "Operations
// Growth Department (OGD)") is still one word too many for a table column at any width without
// wrapping every header to 3+ lines. The full name is never lost - it's set as the column's
// `headerTooltip` wherever this is used, so it's a hover away.
const DEPT_SHORT_NAMES = {
  11: 'OGD',
  20: 'Classroom',
  3: 'Foundation',
  21: 'Little Genie',
  12: 'CSD',
  22: 'Speak Pro',
};

export const getDeptShortName = (deptId, deptName = '') => {
  if (DEPT_SHORT_NAMES[deptId]) return DEPT_SHORT_NAMES[deptId];
  // Departments added after this list was written (see the file-level comment) fall back to a
  // generic shortener rather than showing the full raw name: prefer a "(ABBR)" suffix if the
  // name has one, else the part after a " - " separator (this API's "Project - X" convention).
  const parenMatch = deptName.match(/\(([^)]+)\)\s*$/);
  if (parenMatch) return parenMatch[1];
  const dashIndex = deptName.lastIndexOf(' - ');
  let base = dashIndex !== -1 ? deptName.slice(dashIndex + 3).trim() : deptName;
  // The single most common plain department-naming pattern - "X Department"/"X Dept" reads
  // exactly the same as just "X", and stripping it usually removes the need to truncate at
  // all (e.g. "Technical Department" -> "Technical", not a truncated "Technical Departm…").
  base = base.replace(/\s+(Department|Dept\.?)$/i, '').trim() || base;
  if (base.length <= 18) return base;
  // Still too long (an unusually long name with nothing above to shorten it by) - cut at the
  // last whole word that fits within the cap, never mid-word, so a name that reaches this
  // point degrades to "International…" rather than the "Internation…" a blind character
  // slice would produce.
  const truncated = base.slice(0, 18);
  const lastSpace = truncated.lastIndexOf(' ');
  return `${(lastSpace > 4 ? truncated.slice(0, lastSpace) : truncated).trim()}…`;
};

export const sortDepartments = (departments = []) =>
  [...departments].sort((a, b) => orderIndex(a.deptId) - orderIndex(b.deptId));

export const formatCurrency = (value) => `₹ ${Number(value || 0).toLocaleString('en-IN')}`;

// Compact form for cards/chart (₹90.2L) so big numbers stay scannable; table keeps full precision.
export const formatCompactCurrency = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};

export const formatPercent = (value) => (value === null || value === undefined ? '—' : `${value}%`);

// Config switch for the Daily Breakdown table's per-cell amount heatmap tint (see
// departmentIncomeTrackerTableHeader.js). Off for now - flip to true to bring it back; the
// tint logic itself stays in place rather than being deleted.
export const SHOW_TABLE_AMOUNT_COLORS = false;

// Sentinel `date` for the Daily Breakdown table's month-total footer row (see
// departmentIncomeTrackerTableHeader.js / DepartmentIncomeTracker.jsx) - never a real date, so
// it can't collide with a day row, and lets the header util tell the summary row apart from an
// actual day (e.g. to skip the per-cell click-through-to-detail behaviour on it).
export const MONTH_TOTAL_ROW_ID = '__month_total__';

// Status is paced against how much of the month has elapsed, not a fixed TAR% cutoff - 46%
// TAR on day 15 of 31 is ON PACE (48% of the month gone), not "behind." A flat 50/80% rule
// would misread every department as behind for the first half of every month.
export const TAR_STATUS = {
  NO_TARGET: 'no_target',
  BEHIND: 'behind',
  ON_TRACK: 'on_track',
  AHEAD: 'ahead',
};

export const getPaceStatus = ({ target, revenueToDate, elapsedDays, daysInMonth }) => {
  if (!target || !daysInMonth || elapsedDays <= 0) return TAR_STATUS.NO_TARGET;
  const expectedToDate = target * (elapsedDays / daysInMonth);
  if (expectedToDate <= 0) return TAR_STATUS.NO_TARGET;
  const paceRatio = revenueToDate / expectedToDate;
  if (paceRatio < 0.85) return TAR_STATUS.BEHIND;
  if (paceRatio <= 1.1) return TAR_STATUS.ON_TRACK;
  return TAR_STATUS.AHEAD;
};

export const TAR_STATUS_META = {
  [TAR_STATUS.NO_TARGET]: { label: 'No target set', color: 'default', muiColor: '#94A3B8' },
  [TAR_STATUS.BEHIND]: { label: 'Behind', color: 'error', muiColor: '#EF4444' },
  [TAR_STATUS.ON_TRACK]: { label: 'On track', color: 'warning', muiColor: '#F59E0B' },
  [TAR_STATUS.AHEAD]: { label: 'Ahead', color: 'success', muiColor: '#22C55E' },
};

// "Try a target" - a view-only what-if for a department with no real target set yet. Lives
// entirely in this browser's localStorage, keyed per department+month; never sent to the
// backend, never visible to anyone else. Only meant to fill the gap where a real target
// (target_slot_fill) is 0 - a department that already has one isn't touched by this at all.
const TEMP_TARGET_KEY_PREFIX = 'deptIncomeTracker.tempTarget';
const tempTargetKey = (deptId, month) => `${TEMP_TARGET_KEY_PREFIX}.${deptId}.${month}`;

export const getTempTarget = (deptId, month) => {
  try {
    const raw = window.localStorage.getItem(tempTargetKey(deptId, month));
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

export const setTempTarget = (deptId, month, value) => {
  try {
    window.localStorage.setItem(tempTargetKey(deptId, month), String(value));
  } catch {
    // localStorage unavailable (private browsing, quota, etc.) - the preview still works for
    // this render, it just won't survive a reload.
  }
};

export const clearTempTarget = (deptId, month) => {
  try {
    window.localStorage.removeItem(tempTargetKey(deptId, month));
  } catch {
    // ignore
  }
};
